import { Rate } from 'components/FeedbackPopup/constants'
import { EasyBuilderStructure, PathField } from 'shared/api/requests/easyBuilder/schemas'
import { EasyBuilderWizardType } from 'shared/api/requests/easyBuilder/types'
import { analyticsService } from 'utils/services/analytics'
import { AnalyticsData } from 'utils/services/analytics/types'

export type AnalyticsTriggersOptions =
  | PathField.TR_CGT_ACTIVE
  | PathField.TR_SR_ACTIVE
  | PathField.TR_DM_ACTIVE

export const EasyBuilderAnalytics = {
  /* IG CGT CASE */
  INITIAL_OPENED: (payload: { campaignType: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.INITIAL_OPENED', payload),
  EDIT_MODE_OPENED: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.EDIT_MODE_OPENED', payload),
  TRIGGER_OPTIONS_SWITCHING: (payload: {
    campaignType: string
    flowId?: string
    triggers: Record<string, boolean>
  }) => analyticsService.sendEvent('EASY_BUILDER.TRIGGER_OPTIONS_SWITCHING', payload),
  POST_OPTIONS_SWITCHING: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.POST_OPTIONS_SWITCHING', payload),
  COMMENT_OPTIONS_SWITCHING: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.COMMENT_OPTIONS_SWITCHING', payload),
  OTHER_ACTIONS_CHANGE: (payload: {
    campaignType: string
    flowId?: string
    checked: boolean
    action: string
  }) => analyticsService.sendEvent('EASY_BUILDER.OTHER_ACTIONS_CHANGE', payload),
  OPENING_DM_SWITCHING: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.OPENING_DM_SWITCHING', payload),
  OPENING_DM_HYPERLINK_CLICK: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.OPENING_DM_HYPERLINK.CLICK', payload),
  // Error states in sidebar
  COMMENT_CONTAINS_NEXT_BUTTON_ERROR: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.COMMENT_CONTAINS.NEXT_BTN.CLICK_ERROR', payload),
  DIRECT_NODE_NEXT_BUTTON_ERROR: (payload: {
    campaignType: string
    flowId?: string
    directMessageState?: string
    directLinkState?: string
    directButtonState?: string
  }) => analyticsService.sendEvent('EASY_BUILDER.DIRECT_NODE.NEXT_BTN.CLICK_ERROR', payload),
  GREETING_MESSAGE_NEXT_BUTTON_ERROR: (payload: {
    campaignType: string
    flowId?: string
    welcomeTextState?: string
    welcomeLabelState?: string
  }) => analyticsService.sendEvent('EASY_BUILDER.GREETING_MESSAGE.NEXT_BTN.CLICK_ERROR', payload),
  NEXT_POST_EXISTS_ERROR: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.NEXT_POST_EXISTS.CLICK_ERROR', payload),
  // Preview
  PREVIEW_OPTIONS_SWITCHING: (payload: { campaignType: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.PREVIEW_OPTIONS_SWITCHING', payload),
  PUBLISH_ATTEMPT: (payload: { campaignType: string; wizardType?: EasyBuilderWizardType }) =>
    analyticsService.sendEvent('EASY_BUILDER.PUBLISH_ATTEMPT', payload),
  PUBLISH_SUCCESS: (payload: {
    campaignType: string
    publishPayload: EasyBuilderStructure
    wizardType?: EasyBuilderWizardType
  }) => analyticsService.sendEvent('EASY_BUILDER.PUBLISH_SUCCESS', payload as AnalyticsData),
  PUBLISH_ERROR: (payload: { campaignType: string; errorFieldNames: string[] }) =>
    analyticsService.sendEvent('EASY_BUILDER.PUBLISH_ERROR', payload),
  // Activity tab
  ACTIVITY_PREVIEW_TABS_SWITCHING: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.ACTIVITY_PREVIEW_TABS_SWITCHING', payload),
  ACTIVITY_EDIT_BTN_CLICK: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.ACTIVITY.EDIT_BTN.CLICK', payload),
  ACTIVITY_TEMLATES_CLICK: () =>
    analyticsService.sendEvent('EASY_BUILDER.ACTIVITY.TEMPLATES.CLICK'),
  UPDATE_ATTEMPT: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.UPDATE_ATTEMPT', payload),
  UPDATE_ERROR: (payload: { campaignType: string; flowId?: string; errorFieldNames: string[] }) =>
    analyticsService.sendEvent('EASY_BUILDER.UPDATE_ERROR', payload),
  ACTIVITY_STOP_BTN_CLICK: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.ACTIVITY.STOP_BTN.CLICK', payload),
  STATS_BTN_CLICK: (payload: { campaignType: string; flowId?: string; statType: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.ACTIVITY.STATS_BTN.CLICK', payload),
  STATS_EMAILS_BTN_CLICK: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.ACTIVITY.STATS_EMAILS_BTN.CLICK', payload),
  STATS_EMAILS_BTN_EXPORT_CLICK: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.ACTIVITY.STATS_EMAILS_EXPORT_BTN.CLICK', payload),
  /* IG STORY REPLY CASE */
  STORY_OPTIONS_SWITCHING: (payload: { campaignType: string; flowId?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.STORY_OPTIONS_SWITCHING', payload),
  STORY_OTHER_ACTIONS_CHANGE: (payload: {
    campaignType: string
    flowId?: string
    checked: boolean
    action: string
  }) => analyticsService.sendEvent('EASY_BUILDER.STORY_OTHER_ACTIONS_CHANGE', payload),
  NO_INSTAGRAM_REDIRECT: () => analyticsService.sendEvent('EASY_BUILDER.NO_INSTAGRAM_REDIRECT'),
  CLICK_ADD_LINK_BUTTON: () => analyticsService.sendEvent('EASY_BUILDER.CLICK_ADD_LINK_BUTTON'),
  SAVE_DM_LINK: () => analyticsService.sendEvent('EASY_BUILDER.SAVE_DM_LINK'),
  DELETE_DM_LINK: () => analyticsService.sendEvent('EASY_BUILDER.DELETE_DM_LINK'),
  /* FEEDBACK POPUP */
  USERS_FEEDBACK: (payload: { feedbackReaction: Rate; feedbackComment?: string }) =>
    analyticsService.sendEvent('EASY_BUILDER.USERS_FEEDBACK', payload),
  /* RECOMMENDATIONS */
  RECOMMENDATIONS_SHOWN: () => analyticsService.sendEvent('EASY_BUILDER.RECOMMENDATIONS_SHOWN'),
  RECOMMENDATIONS_SHOW_MORE: () =>
    analyticsService.sendEvent('EASY_BUILDER.RECOMMENDATIONS_SHOW_MORE'),
  RECOMMENDATION_CLICKED: () => analyticsService.sendEvent('EASY_BUILDER.RECOMMENDATION_CLICKED'),
  RECOMMENDATION_MODAL_CTA_CLICKED: () =>
    analyticsService.sendEvent('EASY_BUILDER.RECOMMENDATION_MODAL.CTA_BTN.CLICK'),
  MOBILE_APP_BACK_BUTTON_CLICKED: () =>
    analyticsService.sendEvent('EASY_BUILDER.MOBILE_APP.BACK_BUTTON_CLICKED'),
  MOBILE_APP_INELIGIBLE_MODAL_BACK_BUTTON_CLICKED: () =>
    analyticsService.sendEvent('EASY_BUILDER.MOBILE_APP.INELIGIBLE_MODAL.BACK_BUTTON_CLICKED'),
  MOBILE_APP_CANCEL_BUTTON_CLICKED: () =>
    analyticsService.sendEvent('EASY_BUILDER.MOBILE_APP.CANCEL_BUTTON_CLICKED'),
  DRAFT_MODAL_SHOWN: (type: string) =>
    analyticsService.sendEvent('EASY_BUILDER.DRAFT_MODAL.SHOWN', { type }),
  DRAFT_MODAL_CONTINUE_CLICKED: (type: string) =>
    analyticsService.sendEvent('EASY_BUILDER.DRAFT_MODAL.CONTINUE_CLICKED', { type }),
  DRAFT_MODAL_START_FRESH_CLICKED: (type: string) =>
    analyticsService.sendEvent('EASY_BUILDER.DRAFT_MODAL.START_FRESH_CLICKED', { type }),
  /* WIZARD */
  ONBOARDING_TYPE_SELECTED: (wizardType: EasyBuilderWizardType) =>
    analyticsService.sendEvent('EASY_BUILDER.ONBOARDING.TYPE_SELECTED', { wizardType }),
  ONBOARDING_EXIT_CLICKED: () =>
    analyticsService.sendEvent('EASY_BUILDER.ONBOARDING.EXIT_TO_HOME_CLICKED'),
  ONBOARDING_FINAL_SCREEN_EXIT_CLICKED: () =>
    analyticsService.sendEvent('EASY_BUILDER.ONBOARDING.FINAL_SCREEN_EXIT_TO_HOME_CLICKED'),
  ONBOARDING_NEXT_CLICKED: (wizardType: EasyBuilderWizardType, currentStep: string | null) =>
    analyticsService.sendEvent('EASY_BUILDER.ONBOARDING.NEXT_BUTTON.CLICKED', {
      wizardType,
      currentStep: currentStep || 'last',
    }),
  ONBOARDING_BACK_CLICKED: (wizardType: EasyBuilderWizardType, currentStep: string | null) =>
    analyticsService.sendEvent('EASY_BUILDER.ONBOARDING.BACK_BUTTON.CLICKED', {
      wizardType,
      currentStep: currentStep || 'last',
    }),
  ONBOARDING_GO_LIVE_CLICKED: (wizardType: EasyBuilderWizardType) =>
    analyticsService.sendEvent('EASY_BUILDER.ONBOARDING.GO_LIVE_BUTTON.CLICKED', { wizardType }),
  ONBOARDING_LEAVE_ANYWAY_CLICKED: (wizardType: EasyBuilderWizardType) =>
    analyticsService.sendEvent('EASY_BUILDER.ONBOARDING.LEAVE_ANYWAY.CLICKED', {
      wizardType,
    }),
  ONBOARDING_KEEP_EDITING_CLICKED: (wizardType: EasyBuilderWizardType) =>
    analyticsService.sendEvent('EASY_BUILDER.ONBOARDING.KEEP_EDITING.CLICKED', {
      wizardType,
    }),
  ONBOARDING_EDIT_CLICKED: (wizardType: EasyBuilderWizardType) =>
    analyticsService.sendEvent('EASY_BUILDER.ONBOARDING.EDIT_BUTTON.CLICKED', {
      wizardType,
    }),
  ONBOARDING_STEP_VIEWED: (wizardType: EasyBuilderWizardType, currentStep: string | null) =>
    analyticsService.sendEvent('EASY_BUILDER.ONBOARDING.STEP.VIEWED', {
      wizardType,
      currentStep: currentStep || 'last',
    }),

  // Tailor content experiment
  TAILOR_CONTENT_POPUP_SHOWN: () =>
    analyticsService.sendEvent('EASY_BUILDER.TAILOR_CONTENT.POPUP_SHOWN'),
  TAILOR_CONTENT_POPUP_CLOSED: () =>
    analyticsService.sendEvent('EASY_BUILDER.TAILOR_CONTENT.POPUP_CLOSED'),
  TAILOR_CONTENT_APPLIED: () => analyticsService.sendEvent('EASY_BUILDER.TAILOR_CONTENT.APPLIED'),

  TAILOR_CONTENT_FEEDBACK_SHOWN: () =>
    analyticsService.sendEvent('EASY_BUILDER.TAILOR_CONTENT_FEEDBACK.SHOWN'),
  TAILOR_CONTENT_FEEDBACK_LIKED: () =>
    analyticsService.sendEvent('EASY_BUILDER.TAILOR_CONTENT_FEEDBACK.LIKED'),
  TAILOR_CONTENT_FEEDBACK_DISLIKED: () =>
    analyticsService.sendEvent('EASY_BUILDER.TAILOR_CONTENT_FEEDBACK.DISLIKED'),
  TAILOR_CONTENT_FEEDBACK_CLOSED: () =>
    analyticsService.sendEvent('EASY_BUILDER.TAILOR_CONTENT_FEEDBACK.CLOSED'),
}
